import {
  CompanyActionPermission,
  CompanyPermission,
  EXPENSE_MANAGEMENT_MENU,
} from '../models/company-permission.model';
import {
  EXPENSE_PERMISSION_MODULE,
  ExpenseAction,
  ExpenseActionName,
  ExpenseBackendAction,
} from '../models/expense-actions.model';
import {
  getCompanyPermissionForCompany,
  hasPageUrlInMenu,
} from './company-permission.util';

type ExpenseActionMap = Map<string, boolean>;

function buildExpenseActionMap(
  actionPermissions: CompanyActionPermission[]
): ExpenseActionMap {
  const map: ExpenseActionMap = new Map();

  for (const permission of actionPermissions) {
    if (permission.module === EXPENSE_PERMISSION_MODULE) {
      map.set(permission.action, permission.isAllowed === true);
    }
  }

  return map;
}

function hasExpenseAction(map: ExpenseActionMap, action: string): boolean {
  return map.has(action);
}

function isExpenseActionAllowed(map: ExpenseActionMap, action: string): boolean {
  return map.get(action) === true;
}

export function hasExpenseModuleActionPermissions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  return Boolean(
    companyPermission?.actionPermissions?.some(
      (permission) => permission.module === EXPENSE_PERMISSION_MODULE
    )
  );
}

/**
 * Resolves a frontend Expense action from BE actionPermissions only.
 * No role-map / legacy CRUD fallback.
 */
export function resolveExpenseFeActionFromBeActionMap(
  map: ExpenseActionMap,
  feAction: ExpenseActionName | string,
  companyPermission?: CompanyPermission | null
): boolean | undefined {
  switch (feAction) {
    case ExpenseAction.View: {
      if (isExpenseActionAllowed(map, ExpenseBackendAction.View)) {
        return true;
      }

      if (hasExpenseAction(map, ExpenseBackendAction.View)) {
        return false;
      }

      // BE pagePermissions grant list/view access when View action is omitted.
      if (
        hasPageUrlInMenu(
          companyPermission,
          EXPENSE_MANAGEMENT_MENU,
          'expenses/list'
        )
      ) {
        return true;
      }

      return undefined;
    }

    case ExpenseAction.DeleteDraft: {
      if (isExpenseActionAllowed(map, ExpenseBackendAction.DeleteDraft)) {
        return true;
      }

      if (hasExpenseAction(map, ExpenseBackendAction.DeleteDraft)) {
        return false;
      }

      // BE alias: UpdateDraft covers draft delete when DeleteDraft is omitted.
      if (isExpenseActionAllowed(map, ExpenseBackendAction.UpdateDraft)) {
        return true;
      }

      if (hasExpenseAction(map, ExpenseBackendAction.UpdateDraft)) {
        return false;
      }

      return undefined;
    }

    case ExpenseAction.EditApprovedOrPaymentPending: {
      if (
        isExpenseActionAllowed(map, ExpenseBackendAction.EditApprovedOrPaymentPending)
      ) {
        return true;
      }

      if (hasExpenseAction(map, ExpenseBackendAction.EditApprovedOrPaymentPending)) {
        return false;
      }

      // BE alias: Update covers approved/payment-pending edit when omitted above.
      if (isExpenseActionAllowed(map, ExpenseBackendAction.Update)) {
        return true;
      }

      if (hasExpenseAction(map, ExpenseBackendAction.Update)) {
        return false;
      }

      return undefined;
    }

    default: {
      if (isExpenseActionAllowed(map, feAction)) {
        return true;
      }

      if (hasExpenseAction(map, feAction)) {
        return false;
      }

      return undefined;
    }
  }
}

/** BE-only: true only when Expense actionPermissions (or View via pagePermissions) allow it. */
export function resolveExpenseActionAllowed(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  action: ExpenseActionName | string
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const map = buildExpenseActionMap(actionPermissions);
  const resolved = resolveExpenseFeActionFromBeActionMap(
    map,
    action,
    companyPermission
  );

  if (resolved !== undefined) {
    return resolved;
  }

  return false;
}

export function getAllowedExpenseActions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): Set<string> {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const map = buildExpenseActionMap(actionPermissions);
  const allowed = new Set<string>();

  (Object.values(ExpenseAction) as string[]).forEach((feAction) => {
    if (
      resolveExpenseFeActionFromBeActionMap(map, feAction, companyPermission) ===
      true
    ) {
      allowed.add(feAction);
    }
  });

  return allowed;
}
